import type { IInterceptedRequest, MFAErrorCodes } from '../../domain';
import { EventHandler } from './EventHandler';

const USED = Symbol('Used');
export class InterceptedRequest implements IInterceptedRequest {
  private readonly continueHandler: EventHandler<string>;
  private readonly request: Request;

  private errorCode: MFAErrorCodes | null = null;
  private pendingId!: string;
  private expiryMins!: number;

  // biome-ignore lint/correctness/noUnusedPrivateClassMembers: biome doesn't seem to be able to follow super privates
  private [USED]: boolean;

  constructor(
    request: Request,
    private readonly onCancel: () => void,
  ) {
    this.continueHandler = new EventHandler<string>();
    this.request = request.clone();
    this[USED] = false;
  }

  public setPendingId(pendingId: string) {
    this.pendingId = pendingId;
    this.request.headers.set('x-mfa-pending-id', this.pendingId);
  }

  public getPendingId(): string {
    return this.pendingId;
  }

  public getRequest(): Request {
    return this.request.clone();
  }

  public continue(challengeCode: string) {
    if (this[USED]) throw new Error('MFA challenge already completed');
    this[USED] = true;
    if (!challengeCode)
      throw new Error('MFA challenge code is required to continue the request');

    this.request.headers.set('x-mfa-challenge-code', challengeCode);
    this.continueHandler.trigger(challengeCode);
  }

  public cancel() {
    this[USED] = true;
    this.onCancel();
  }

  public onContinue(callback: (challengeCode: string) => void) {
    this.continueHandler.addListener(callback);
  }

  public setError(error: MFAErrorCodes | null) {
    this.errorCode = error;
  }

  public getError(): MFAErrorCodes | null {
    return this.errorCode;
  }

  public setExpiryMins(expiryMins: number) {
    this.expiryMins = expiryMins;
  }

  public getExpiryMins() {
    return this.expiryMins;
  }
}
